import React from 'react';
import { Member, SplitShares } from 'types/splits.types';
import {
  Control,
  UseFormGetValues,
  UseFormSetValue,
  UseFormTrigger,
} from 'react-hook-form';
import { getAvatarImage } from 'app/utils/utils';
import { ContributionClassRolesDDL } from 'types/contribution';
import { ReactComponent as CloseIcon } from 'resources/images/close.svg';
import {
  ListItemAvatar,
  ListItemText,
  TableList,
} from 'app/components/ChakraTableList';
import { SplitTextField } from 'app/components/Song/Splits/SplitTextField';
import {
  Button,
  Icon,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Portal,
} from '@chakra-ui/react';
import { formatValue } from '../utils';

export interface ProposeSplitListProps {
  splitMembers: Member[];
  membersSelectionList?: Member[];
  control: Control<SplitShares>;
  getValues: UseFormGetValues<SplitShares>;
  setValue: UseFormSetValue<SplitShares>;
  trigger: UseFormTrigger<SplitShares>;
  entriesSum?: number | string;
  contributionRolesDDL?: ContributionClassRolesDDL;
  removeMember: (member: Member) => void;
  onMemberSelect: (member: Member) => void;
  isLoading: boolean;
}

export const ProposeSplitList: React.FC<ProposeSplitListProps> = ({
  splitMembers,
  membersSelectionList = [],
  control,
  getValues,
  setValue,
  trigger,
  entriesSum = 0,
  contributionRolesDDL,
  removeMember,
  onMemberSelect,
  isLoading,
}) => {
  const splitEqually = async () => {
    const memberEntries: SplitShares['members'] = getValues('members');
    const splitValue = Math.floor(10000 / memberEntries.length);
    let sum = 0;
    const values = memberEntries.map((member, i, array) => {
      sum += splitValue;
      return {
        ...member,
        share:
          i === array.length - 1
            ? formatValue((splitValue + 10000 - sum) / 100)
            : formatValue(splitValue / 100),
      };
    });
    setValue('members', values, { shouldDirty: true });
    await trigger();
  };
  const splitTotal = Number(entriesSum).toFixed(2);

  if (isLoading) {
    return null;
  }

  return (
    <TableList
      scrollable
      data-testid="propose-split-list"
      wrapperSx={{
        ' div[role=row]': {
          svg: {
            color: 'iconBase',
          },
          ':hover': {
            svg: {
              color: 'M.70',
            },
          },
          ':last-child': {
            bg: 'transparent',
            borderColor: 'transparent',
          },
        },
      }}
      memoizeColumnFormatter
      columns={[
        {
          key: 'avatar',
          name: '',
          accessory: true,
          colSpan: ({ type }) => (type === 'SUMMARY' ? 2 : 1),
        },
        { key: 'name', name: 'Name' },
        { key: 'percentage', name: 'Percentage', width: '50%' },
        { key: 'remove', name: '', width: 40 },
      ]}
      rows={splitMembers.map((member, index) => {
        const roles = member?.roles
          ?.map((role) => role && contributionRolesDDL?.[role]?.name)
          .join(', ');
        return {
          avatar: (
            <ListItemAvatar
              initials={member?.profile?.initials}
              colors={member?.profile?.avatarColors}
              src={getAvatarImage(member?.profile?.profileImages)}
            />
          ),
          name: (
            <ListItemText
              primary={member.profile?.displayName}
              title={member.profile?.displayName}
              secondary={roles}
              secondaryTextProps={{
                fontSize: 'xs',
                color: 'textSubdued',
              }}
            />
          ),
          percentage: (
            <SplitTextField
              control={control}
              index={index}
              width="72px"
              height="40px"
            />
          ),
          remove: splitMembers.length > 1 && (
            <Icon
              as={CloseIcon}
              data-testid="remove-member"
              aria-label="Remove member"
              cursor="pointer"
              w={5}
              h={5}
              onClick={() => removeMember(member)}
            />
          ),
        };
      })}
      summaryRows={[
        {
          percentage: (
            <ListItemText
              data-testid="split-total"
              primaryTextProps={{
                pl: 3,
                color: Number(entriesSum) > 100 ? 'textError' : 'textBase',
              }}
              primary={
                <>
                  <strong>Total:</strong> {`${splitTotal}%`}
                </>
              }
            />
          ),
        },
        {
          avatar: (
            <Menu>
              <MenuButton
                as={Button}
                size="sm"
                variant="secondary"
                isDisabled={membersSelectionList.length === 0}
              >
                Add Members
              </MenuButton>
              <Portal>
                <MenuList
                  as="ul"
                  sx={{
                    overflowY: 'auto',
                    maxH: '290px',
                  }}
                >
                  {membersSelectionList.map((member) => (
                    <MenuItem
                      as="li"
                      px={3}
                      py={1}
                      gap={1}
                      h={12}
                      minW={258}
                      cursor="pointer"
                      onClick={() => onMemberSelect(member)}
                    >
                      <ListItemAvatar
                        initials={member?.profile?.initials}
                        src={getAvatarImage(member?.profile?.profileImages)}
                        colors={member?.profile?.avatarColors}
                      />
                      <ListItemText
                        data-testid="member-display-name"
                        primary={member?.profile?.displayName}
                        title={member?.profile?.displayName}
                      />
                    </MenuItem>
                  ))}
                </MenuList>
              </Portal>
            </Menu>
          ),
          percentage: (
            <Button variant="secondary" size="sm" ml={3} onClick={splitEqually}>
              Split equally
            </Button>
          ),
        },
      ]}
    />
  );
};
